/**
 * Scene background: solid color + radial gradient vignette shader overlay.
 *
 * The gradient uses a full-screen quad rendered at renderOrder=-1 with
 * depthTest/depthWrite disabled so it sits behind everything else.
 * A dithering noise term prevents visible color banding on smooth gradients.
 */

import { useMemo, useEffect } from 'react';
import * as THREE from 'three';

const gradientVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 1.0, 1.0);
  }
`;

const gradientFragmentShader = `
  uniform vec3 uColor;
  varying vec2 vUv;

  // Simple hash for dithering to eliminate banding
  float rand(vec2 co) {
    return fract(sin(dot(co, vec2(12.9898, 78.233))) * 43758.5453);
  }

  void main() {
    vec2 center = vUv - 0.5;
    float dist = length(center);
    // Smooth wide vignette — gentle falloff
    float vignette = dist * dist * 1.6;
    // Center matches chosen color, edges darken subtly
    vec3 col = uColor - uColor * vignette * 0.18;
    // Dither to break banding (+-0.5/255 noise)
    col += (rand(gl_FragCoord.xy) - 0.5) / 255.0;
    gl_FragColor = vec4(col, 1.0);
  }
`;

function GradientOverlay({ color }: { color: string }) {
  const uniforms = useMemo(
    () => ({
      uColor: { value: new THREE.Color(color) },
    }),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Update the uniform when the color prop changes (imperative — avoids recreating the material)
  useEffect(() => {
    uniforms.uColor.value.set(color);
  }, [color, uniforms]);

  return (
    <mesh frustumCulled={false} renderOrder={-1}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        vertexShader={gradientVertexShader}
        fragmentShader={gradientFragmentShader}
        uniforms={uniforms}
        depthTest={false}
        depthWrite={false}
      />
    </mesh>
  );
}

export function SceneBackground({ color }: { color: string }) {
  return (
    <>
      <color attach="background" args={[color]} />
      <GradientOverlay color={color} />
    </>
  );
}
